import { RefObject, useEffect, useRef } from 'react'

interface IExpansionProgress {
  keys: readonly string[]
  attempted: Set<string>
  pending?: string
}

const HIERARCHY_BUTTON_PREFIX = 'select-hierarchy-button-'

export const useSingleRootExpansion = (
  rootRef: RefObject<HTMLDivElement | null>,
  keys: readonly string[]
) => {
  const progressRef = useRef<IExpansionProgress | null>(null)

  useEffect(() => {
    if (!keys.length) return
    const table = rootRef.current?.closest('[data-testid^="table-TableMode-"]')
    if (!table) return

    if (progressRef.current?.keys !== keys) {
      progressRef.current = { keys, attempted: new Set() }
    }
    const progress = progressRef.current
    const allowedKeys = new Set(keys)

    const expandNext = () => {
      const buttons = Array.from(
        table.querySelectorAll<HTMLButtonElement>(`[data-testid^="${HIERARCHY_BUTTON_PREFIX}"]`)
      )
      const getKey = (button: HTMLButtonElement) =>
        button.getAttribute('data-testid')?.slice(HIERARCHY_BUTTON_PREFIX.length) ?? ''

      // The standard table opens nodes asynchronously. Wait for the current caret to turn down
      // before opening another branch, so sibling updates cannot overwrite each other's state.
      if (progress.pending) {
        const pendingButton = buttons.find((button) => getKey(button) === progress.pending)
        if (!pendingButton?.querySelector('.kbase-caret-down')) return
        progress.pending = undefined
      }

      const next = buttons.find((button) => {
        const key = getKey(button)
        return (
          allowedKeys.has(key) &&
          !progress.attempted.has(key) &&
          Boolean(button.querySelector('.kbase-caret-right'))
        )
      })
      if (!next) return

      const key = getKey(next)
      progress.attempted.add(key)
      progress.pending = key
      next.click()
    }

    const observer = new MutationObserver(expandNext)
    observer.observe(table, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    })
    expandNext()

    return () => observer.disconnect()
  }, [keys, rootRef])
}
